import { supabase } from '../lib/supabase';
import type {
  UserCareerContext,
  UserProfileContext,
  CareerIntelligenceContext,
  SkillsTrackerContext,
  BRAGContext,
  DREAMContext,
  BehaviorContext,
  ResumeContext,
  JobSearchContext,
  CoachingInsights,
  CoachingTriggerEvent,
  ContextAggregationOptions
} from '../types/ai-coach-context.types';

export class ContextAggregationService {
  private static instance: ContextAggregationService;
  private contextCache = new Map<string, { context: UserCareerContext; timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  private constructor() {}

  static getInstance(): ContextAggregationService {
    if (!ContextAggregationService.instance) {
      ContextAggregationService.instance = new ContextAggregationService();
    }
    return ContextAggregationService.instance;
  }

  async getUserContext(
    userId: string,
    options: ContextAggregationOptions = {}
  ): Promise<UserCareerContext> {
    const cacheKey = `${userId}-${JSON.stringify(options)}`;
    const cached = this.contextCache.get(cacheKey);

    if (cached && !options.forceRefresh && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.context;
    }

    const context = await this.aggregateContext(userId, options);
    this.contextCache.set(cacheKey, { context, timestamp: Date.now() });

    return context;
  }

  private async aggregateContext(
    userId: string,
    options: ContextAggregationOptions
  ): Promise<UserCareerContext> {
    const [profile, careerIntelligence, skills, achievements, aspirations, resume, jobSearch, behavior] =
      await Promise.all([
        this.loadUserProfile(userId),
        options.includeReport !== false ? this.loadCareerIntelligence(userId) : null,
        options.includeSkills !== false ? this.loadSkillsContext(userId) : null,
        options.includeBRAG !== false ? this.loadBRAGContext(userId) : null,
        options.includeDREAM !== false ? this.loadDREAMContext(userId) : null,
        this.loadResumeContext(userId),
        this.loadJobSearchContext(userId),
        options.includeBehavior !== false ? this.loadBehaviorContext(userId) : null
      ]);

    const fullContext = {
      profile,
      careerIntelligence: careerIntelligence || {} as CareerIntelligenceContext,
      skills: skills || {} as SkillsTrackerContext,
      achievements: achievements || {} as BRAGContext,
      aspirations: aspirations || {} as DREAMContext,
      resume: resume || this.getEmptyResumeContext(),
      jobSearch: jobSearch || this.getEmptyJobSearchContext(),
      behavior: behavior || {} as BehaviorContext
    };

    const insights = await this.generateCoachingInsights(fullContext);
    const triggers = await this.generateCoachingTriggers(fullContext, insights);

    return {
      userId,
      lastUpdated: new Date().toISOString(),
      contextVersion: '1.1.0',
      profile,
      careerIntelligence: careerIntelligence || this.getEmptyCareerIntelligence(),
      skills: skills || this.getEmptySkillsContext(),
      achievements: achievements || this.getEmptyBRAGContext(),
      aspirations: aspirations || this.getEmptyDREAMContext(),
      resume: resume || this.getEmptyResumeContext(),
      jobSearch: jobSearch || this.getEmptyJobSearchContext(),
      behavior: behavior || this.getEmptyBehaviorContext(),
      insights,
      triggers
    };
  }

  private async loadUserProfile(userId: string): Promise<UserProfileContext> {
    const { data: profile } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    // Get user email from current session instead of admin API
    const { data: { user } } = await supabase.auth.getUser();

    return {
      email: user?.email || '',
      fullName: profile?.full_name || 'User',
      currentRole: profile?.current_role,
      yearsExperience: profile?.years_of_experience,
      industry: profile?.industry,
      location: profile?.location,
      careerPhase: this.determineCareerPhase(profile?.years_of_experience),
      preferences: {
        coachingStyle: profile?.coaching_style || 'supportive',
        communicationFrequency: profile?.communication_frequency || 'weekly',
        focusAreas: profile?.focus_areas || []
      }
    };
  }

  private async loadCareerIntelligence(userId: string): Promise<CareerIntelligenceContext> {
    const { data: reports } = await supabase
      .from('reports')
      .select('id, created_at, report_data')
      .eq('user_id', userId)
      .eq('status', 'completed')
      .order('created_at', { ascending: false })
      .limit(1);

    if (!reports || reports.length === 0) {
      return this.getEmptyCareerIntelligence();
    }

    const report = reports[0];
    const reportData = report.report_data as any;

    return {
      hasReport: true,
      reportId: report.id,
      reportDate: report.created_at,
      sections: this.extractReportSections(reportData),
      keyInsights: this.extractKeyInsights(reportData),
      strengthAreas: this.extractStrengthAreas(reportData),
      developmentAreas: this.extractDevelopmentAreas(reportData)
    };
  }

  private async loadSkillsContext(userId: string): Promise<SkillsTrackerContext> {
    const { data: skills } = await supabase
      .from('user_skills_tracker')
      .select(`
        *,
        skill:skills_master (
          id,
          skill_name,
          category
        )
      `)
      .eq('user_id', userId)
      .eq('is_deleted', false);

    const { data: progressUpdates } = await supabase
      .from('skill_progress_updates')
      .select('*')
      .in('user_skill_id', skills?.map(s => s.id) || [])
      .order('created_at', { ascending: false })
      .limit(50);

    const totalHours = progressUpdates?.reduce((sum, u) => sum + (u.hours_invested || 0), 0) || 0;
    const activeSkills = skills?.filter(s => s.status === 'active') || [];
    const completedSkills = skills?.filter(s => s.status === 'completed') || [];

    const now = new Date();
    const stagnantSkills = activeSkills.filter(skill => {
      const lastUpdate = new Date(skill.last_progress_update || skill.created_at);
      const daysSince = Math.floor((now.getTime() - lastUpdate.getTime()) / (1000 * 60 * 60 * 24));
      return daysSince > 14; // No progress in 2 weeks
    });

    const prioritySkills = skills
      ?.filter(s => s.status === 'active')
      .sort((a, b) => a.priority - b.priority)
      .slice(0, 5)
      .map(s => ({
        id: s.id,
        skillName: s.skill.skill_name,
        category: s.skill.category,
        priority: s.priority,
        currentLevel: s.current_proficiency_level,
        targetLevel: s.target_proficiency_level,
        progress: s.progress_percentage,
        hoursInvested: progressUpdates?.filter(p => p.user_skill_id === s.id)
          .reduce((sum, p) => sum + (p.hours_invested || 0), 0) || 0,
        lastUpdated: s.last_progress_update || s.created_at,
        estimatedCompletion: s.target_completion_date || '',
        isStagnant: stagnantSkills.some(ss => ss.id === s.id)
      })) || [];

    return {
      totalSkills: skills?.length || 0,
      activeSkills: activeSkills.length,
      completedSkills: completedSkills.length,
      averageProgress: skills && skills.length > 0
        ? Math.round(skills.reduce((sum, s) => sum + s.progress_percentage, 0) / skills.length)
        : 0,
      totalHoursInvested: parseFloat(totalHours.toFixed(1)),
      prioritySkills,
      recentActivity: progressUpdates?.slice(0, 10).map(u => ({
        date: u.created_at,
        skillId: u.user_skill_id,
        skillName: skills?.find(s => s.id === u.user_skill_id)?.skill.skill_name || '',
        hours: u.hours_invested || 0,
        activityType: u.activity_type || '',
        progressIncrement: u.progress_increment || 0,
        confidenceLevel: u.confidence_level || 5
      })) || [],
      progressVelocity: this.calculateProgressVelocity(completedSkills, skills),
      timeAllocationPattern: this.calculateTimeAllocation(skills, progressUpdates),
      proficiencyDistribution: this.calculateProficiencyDistribution(skills),
      stagnationFlags: {
        hasStagnantSkills: stagnantSkills.length > 0,
        stagnantSkillIds: stagnantSkills.map(s => s.id),
        daysSinceLastUpdate: progressUpdates && progressUpdates.length > 0
          ? Math.floor((now.getTime() - new Date(progressUpdates[0].created_at).getTime()) / (1000 * 60 * 60 * 24))
          : 999
      }
    };
  }

  private async loadBRAGContext(userId: string): Promise<BRAGContext> {
    const { data: achievements } = await supabase
      .from('brag_entries')
      .select('*')
      .eq('user_id', userId)
      .order('achievement_date', { ascending: false });

    if (!achievements || achievements.length === 0) {
      return this.getEmptyBRAGContext();
    }

    const categories: { [key: string]: number } = {};
    achievements.forEach(a => {
      const cat = a.category || 'Other';
      categories[cat] = (categories[cat] || 0) + 1;
    });

    return {
      totalAchievements: achievements.length,
      lastUpdated: achievements[0]?.created_at,
      achievements: achievements.slice(0, 20).map(a => ({
        id: a.id,
        title: a.title,
        description: a.description,
        category: a.category || 'Other',
        date: a.achievement_date,
        impact: a.impact || '',
        quantifiableResults: a.quantifiable_metrics
      })),
      categories,
      impactMetrics: {
        quantifiedResults: achievements.filter(a => a.quantifiable_metrics).length,
        revenueImpact: 0, // TODO: Extract from metrics
        teamSize: 0, // TODO: Extract from metrics
        projectsCompleted: achievements.filter(a => a.category === 'Project').length
      }
    };
  }

  private async loadDREAMContext(userId: string): Promise<DREAMContext> {
    const { data: dreamData } = await supabase
      .from('dream_assessments')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (!dreamData) {
      return this.getEmptyDREAMContext();
    }

    return {
      hasAssessment: true,
      lastUpdated: dreamData.updated_at,
      careerAspirations: {
        shortTerm: dreamData.short_term_goals || [],
        longTerm: dreamData.long_term_goals || [],
        dreamRole: dreamData.dream_role,
        targetCompanies: dreamData.target_companies || []
      },
      motivations: {
        primary: dreamData.primary_motivations || [],
        workValues: dreamData.work_values || [],
        dealBreakers: dreamData.deal_breakers || []
      },
      preferences: {
        workStyle: dreamData.work_style || '',
        teamSize: dreamData.team_size_preference || '',
        management: dreamData.management_preference || '',
        travel: dreamData.travel_preference || '',
        remote: dreamData.remote_preference || ''
      },
      alignment: {
        currentVsDesired: dreamData.alignment_score || 50,
        gapAreas: dreamData.gap_areas || []
      }
    };
  }

  private async loadBehaviorContext(userId: string): Promise<BehaviorContext> {
    // This would integrate with analytics/logging system
    // For now, return basic structure
    return {
      engagementMetrics: {
        loginFrequency: 0,
        lastLoginDate: new Date().toISOString(),
        averageSessionDuration: 0,
        featureUsage: {}
      },
      progressPatterns: {
        consistencyScore: 0,
        streakDays: 0,
        longestStreak: 0,
        mostActiveDay: 'Monday',
        mostActiveTime: '9am'
      },
      interactionPatterns: {
        reportsGenerated: 0,
        skillsAdded: 0,
        progressLogsCount: 0
      },
      coachingHistory: {
        totalSessions: 0,
        topicsDiscussed: [],
        actionItemsCompleted: 0,
        actionItemsPending: 0
      }
    };
  }

  private async generateCoachingInsights(context: {
    profile: UserProfileContext;
    skills: SkillsTrackerContext;
    achievements: BRAGContext;
    aspirations: DREAMContext;
  }): Promise<CoachingInsights> {
    const urgentItems = [];
    const opportunities = [];

    // Check for stagnation
    if (context.skills.stagnationFlags.hasStagnantSkills) {
      urgentItems.push({
        id: `stagnation-${Date.now()}`,
        type: 'stagnation' as const,
        severity: 'warning' as const,
        title: 'Skills Development Stagnation Detected',
        message: `${context.skills.stagnationFlags.stagnantSkillIds.length} skills haven't been updated in over 2 weeks`,
        actionable: true,
        suggestedActions: [
          'Log progress on priority skills',
          'Reassess skill priorities',
          'Set aside dedicated learning time'
        ],
        createdAt: new Date().toISOString()
      });
    }

    // Determine motivation level
    const motivationLevel = this.assessMotivationLevel(context);

    return {
      currentFocus: context.skills.prioritySkills.slice(0, 3).map(s => s.skillName),
      urgentItems,
      opportunities,
      motivationLevel,
      riskFactors: context.skills.stagnationFlags.hasStagnantSkills ? ['Skill development stagnation'] : [],
      strengths: ['Consistent tracking', 'Clear goals'],
      recommendations: [],
      nextBestActions: []
    };
  }

  private async loadResumeContext(userId: string): Promise<ResumeContext> {
    const { data: documents } = await supabase
      .from('documents')
      .select('*')
      .eq('user_id', userId)
      .eq('type', 'resume')
      .order('created_at', { ascending: false })
      .limit(1);

    if (!documents || documents.length === 0) {
      return this.getEmptyResumeContext();
    }

    const resume = documents[0];
    const extractedData = resume.extracted_data || {};

    return {
      hasResume: true,
      uploadedAt: resume.created_at,
      extractedAt: resume.extracted_at,
      experience: extractedData.experience || [],
      education: extractedData.education || [],
      skills: extractedData.skills || [],
      certifications: extractedData.certifications || [],
      summary: extractedData.summary,
      totalYears: extractedData.total_years || 0,
      companiesWorked: extractedData.companies || [],
      rolesHeld: extractedData.roles || [],
      keyHighlights: extractedData.highlights || [],
      careerProgression: {
        direction: extractedData.progression_direction || 'lateral',
        titleProgression: extractedData.title_progression || [],
        industryChanges: extractedData.industry_changes || 0
      }
    };
  }

  private async loadJobSearchContext(userId: string): Promise<JobSearchContext> {
    const { data: goals } = await supabase
      .from('job_search_goals')
      .select('*')
      .eq('user_id', userId)
      .eq('is_actively_searching', true)
      .maybeSingle();

    const { data: applications } = await supabase
      .from('job_applications')
      .select('*')
      .eq('user_id', userId)
      .eq('is_archived', false)
      .order('applied_at', { ascending: false })
      .limit(50);

    const { data: interviews } = await supabase
      .from('job_interviews')
      .select('*')
      .eq('user_id', userId)
      .order('scheduled_at', { ascending: false })
      .limit(50);

    const { data: offers } = await supabase
      .from('job_offers')
      .select('*')
      .eq('user_id', userId)
      .order('offer_received_at', { ascending: false })
      .limit(20);

    if (!applications || applications.length === 0) {
      return this.getEmptyJobSearchContext();
    }

    // Calculate statistics
    const totalApps = applications.length;
    const appsWithInterviews = new Set(interviews?.map(i => i.application_id) || []).size;
    const appsWithOffers = new Set(offers?.map(o => o.application_id) || []).size;

    const responseRate = totalApps > 0 ? (appsWithInterviews / totalApps) * 100 : 0;
    const interviewConversionRate = appsWithInterviews > 0 ? (appsWithOffers / appsWithInterviews) * 100 : 0;
    const offerRate = totalApps > 0 ? (appsWithOffers / totalApps) * 100 : 0;

    // Find most recent activity
    const allDates = [
      ...(applications?.map(a => a.applied_at).filter(Boolean) || []),
      ...(interviews?.map(i => i.scheduled_at).filter(Boolean) || []),
      ...(offers?.map(o => o.offer_received_at).filter(Boolean) || [])
    ];
    const mostRecentActivity = allDates.length > 0
      ? allDates.sort().reverse()[0]
      : undefined;

    // Count active interviews and pending offers
    const now = new Date();
    const activeInterviews = interviews?.filter(i =>
      i.interview_outcome === 'pending' &&
      i.scheduled_at &&
      new Date(i.scheduled_at) >= now
    ).length || 0;

    const pendingOffers = offers?.filter(o =>
      o.decision_status === 'pending'
    ).length || 0;

    return {
      isActivelySearching: goals?.is_actively_searching || false,
      searchStartDate: goals?.search_started_at,
      targetRoles: goals?.target_roles || [],
      targetCompanies: goals?.target_companies || [],
      applications: applications || [],
      interviews: interviews || [],
      offers: offers || [],
      stats: {
        totalApplications: totalApps,
        responseRate: parseFloat(responseRate.toFixed(2)),
        interviewConversionRate: parseFloat(interviewConversionRate.toFixed(2)),
        offerRate: parseFloat(offerRate.toFixed(2)),
        avgTimeToResponse: 0 // TODO: Calculate from data
      },
      mostRecentActivity,
      activeInterviews,
      pendingOffers
    };
  }

  private async generateCoachingTriggers(
    context: any,
    insights: CoachingInsights
  ): Promise<CoachingTriggerEvent[]> {
    const triggers: CoachingTriggerEvent[] = [];
    const now = new Date().toISOString();

    // Stagnation trigger
    if (context.skills?.stagnationFlags?.hasStagnantSkills) {
      triggers.push({
        type: 'stagnation',
        priority: 8,
        context: {
          stagnantSkills: context.skills.stagnationFlags.stagnantSkillIds,
          daysSinceUpdate: context.skills.stagnationFlags.daysSinceLastUpdate
        },
        triggeredAt: now,
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        handled: false
      });
    }

    // Milestone trigger (skill completion)
    if (context.skills?.completedSkills > 0) {
      const recentCompletions = context.skills.recentActivity?.filter(
        (a: any) => a.progressIncrement >= 100
      ) || [];

      if (recentCompletions.length > 0) {
        triggers.push({
          type: 'milestone',
          priority: 5,
          context: {
            completedSkills: recentCompletions.map((a: any) => a.skillName)
          },
          triggeredAt: now,
          handled: false
        });
      }
    }

    // Opportunity trigger (pending offers)
    if (context.jobSearch?.pendingOffers > 0) {
      triggers.push({
        type: 'opportunity',
        priority: 9,
        context: {
          pendingOffers: context.jobSearch.pendingOffers,
          offers: context.jobSearch.offers
        },
        triggeredAt: now,
        expiresAt: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString(),
        handled: false
      });
    }

    return triggers;
  }

  // Helper methods
  private determineCareerPhase(years?: number): 'early' | 'mid' | 'senior' | 'executive' | 'transitioning' {
    if (!years) return 'transitioning';
    if (years < 3) return 'early';
    if (years < 7) return 'mid';
    if (years < 15) return 'senior';
    return 'executive';
  }

  private extractReportSections(reportData: any): any {
    return {}; // TODO: Implement report parsing
  }

  private extractKeyInsights(reportData: any): string[] {
    return [];
  }

  private extractStrengthAreas(reportData: any): string[] {
    return [];
  }

  private extractDevelopmentAreas(reportData: any): string[] {
    return [];
  }

  private calculateProgressVelocity(completedSkills: any[], allSkills: any[]): number {
    return 0; // TODO: Calculate based on completion dates
  }

  private calculateTimeAllocation(skills: any[], progressUpdates: any[]): { [category: string]: number } {
    return {};
  }

  private calculateProficiencyDistribution(skills: any[]): { [level: string]: number } {
    const dist: { [level: string]: number } = {};
    skills?.forEach(s => {
      const level = s.current_proficiency_level;
      dist[level] = (dist[level] || 0) + 1;
    });
    return dist;
  }

  private assessMotivationLevel(context: any): 'low' | 'medium' | 'high' {
    if (context.skills.stagnationFlags.daysSinceLastUpdate > 30) return 'low';
    if (context.skills.stagnationFlags.daysSinceLastUpdate > 14) return 'medium';
    return 'high';
  }

  // Empty context generators
  private getEmptyCareerIntelligence(): CareerIntelligenceContext {
    return {
      hasReport: false,
      sections: {},
      keyInsights: [],
      strengthAreas: [],
      developmentAreas: []
    };
  }

  private getEmptySkillsContext(): SkillsTrackerContext {
    return {
      totalSkills: 0,
      activeSkills: 0,
      completedSkills: 0,
      averageProgress: 0,
      totalHoursInvested: 0,
      prioritySkills: [],
      recentActivity: [],
      progressVelocity: 0,
      timeAllocationPattern: {},
      proficiencyDistribution: {},
      stagnationFlags: {
        hasStagnantSkills: false,
        stagnantSkillIds: [],
        daysSinceLastUpdate: 0
      }
    };
  }

  private getEmptyBRAGContext(): BRAGContext {
    return {
      totalAchievements: 0,
      achievements: [],
      categories: {},
      impactMetrics: {
        quantifiedResults: 0,
        revenueImpact: 0,
        teamSize: 0,
        projectsCompleted: 0
      }
    };
  }

  private getEmptyDREAMContext(): DREAMContext {
    return {
      hasAssessment: false,
      careerAspirations: {
        shortTerm: [],
        longTerm: []
      },
      motivations: {
        primary: [],
        workValues: [],
        dealBreakers: []
      },
      preferences: {
        workStyle: '',
        teamSize: '',
        management: '',
        travel: '',
        remote: ''
      },
      alignment: {
        currentVsDesired: 0,
        gapAreas: []
      }
    };
  }

  private getEmptyBehaviorContext(): BehaviorContext {
    return {
      engagementMetrics: {
        loginFrequency: 0,
        lastLoginDate: '',
        averageSessionDuration: 0,
        featureUsage: {}
      },
      progressPatterns: {
        consistencyScore: 0,
        streakDays: 0,
        longestStreak: 0,
        mostActiveDay: '',
        mostActiveTime: ''
      },
      interactionPatterns: {
        reportsGenerated: 0,
        skillsAdded: 0,
        progressLogsCount: 0
      },
      coachingHistory: {
        totalSessions: 0,
        topicsDiscussed: [],
        actionItemsCompleted: 0,
        actionItemsPending: 0
      }
    };
  }

  private getEmptyResumeContext(): ResumeContext {
    return {
      hasResume: false,
      experience: [],
      education: [],
      skills: [],
      certifications: [],
      totalYears: 0,
      companiesWorked: [],
      rolesHeld: [],
      keyHighlights: [],
      careerProgression: {
        direction: 'lateral',
        titleProgression: [],
        industryChanges: 0
      }
    };
  }

  private getEmptyJobSearchContext(): JobSearchContext {
    return {
      isActivelySearching: false,
      targetRoles: [],
      targetCompanies: [],
      applications: [],
      interviews: [],
      offers: [],
      stats: {
        totalApplications: 0,
        responseRate: 0,
        interviewConversionRate: 0,
        offerRate: 0,
        avgTimeToResponse: 0
      },
      activeInterviews: 0,
      pendingOffers: 0
    };
  }

  clearCache(userId?: string): void {
    if (userId) {
      Array.from(this.contextCache.keys())
        .filter(key => key.startsWith(userId))
        .forEach(key => this.contextCache.delete(key));
    } else {
      this.contextCache.clear();
    }
  }
}

export const contextAggregation = ContextAggregationService.getInstance();
